import React, { useEffect, useState } from 'react'
import { HiDeviceMobile } from 'react-icons/hi'
import { toast } from 'react-toastify'
import { appRef } from '../../app'
import { listMySessions, revokeMySession, revokeOtherSessions, type IUserSession } from '../../api/security'
import { getLocale } from '../../locale'

export const ConnectedDevicesPage: React.FC = () => {
    const isHebrew = getLocale() === 'he'
    const [sessions, setSessions] = useState<IUserSession[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        appRef.setHeader(<HiDeviceMobile className="me-2" size={28} />, () => (isHebrew ? 'מכשירים מחוברים' : 'Connected Devices'))
    }, [isHebrew])

    const load = async () => {
        setLoading(true)
        try {
            const response = await listMySessions()
            setSessions(response.data)
        } catch (error: any) {
            toast.error(error?.message || (isHebrew ? 'טעינת הסשנים נכשלה' : 'Failed to load sessions'))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="space-y-5">
            <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-5 flex flex-wrap gap-3 items-center">
                <button className="rounded bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2" onClick={load}>
                    {isHebrew ? 'רענן' : 'Refresh'}
                </button>
                <button
                    className="rounded bg-red-600 hover:bg-red-700 text-white px-4 py-2"
                    onClick={async () => {
                        try {
                            await revokeOtherSessions()
                            toast.success(isHebrew ? 'סשנים אחרים בוטלו' : 'Other sessions revoked')
                            await load()
                        } catch (error: any) {
                            toast.error(error?.message || 'Failed')
                        }
                    }}
                >
                    {isHebrew ? 'נתק מכשירים אחרים' : 'Revoke Other Devices'}
                </button>
            </div>

            <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-5">
                {loading ? (
                    <div className="text-gray-600 dark:text-gray-300">{isHebrew ? 'טוען...' : 'Loading...'}</div>
                ) : (
                    <div className="space-y-3">
                        {sessions.map(session => (
                            <div key={session.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap items-center justify-between gap-3">
                                <div className="space-y-1 text-sm text-gray-800 dark:text-gray-100">
                                    <div className="font-semibold text-gray-900 dark:text-gray-100">
                                        {session.deviceName || (isHebrew ? 'מכשיר לא מזוהה' : 'Unknown device')} ({session.devicePlatform || '-'})
                                    </div>
                                    <div className="text-gray-700 dark:text-gray-200">
                                        {isHebrew ? 'נוצר:' : 'Created:'} {new Date(session.createdAt).toLocaleString()}
                                    </div>
                                    <div className="text-gray-700 dark:text-gray-200">
                                        {isHebrew ? 'פעיל עד:' : 'Expires:'} {new Date(session.expiresAt).toLocaleString()}
                                    </div>
                                    <div className="text-gray-700 dark:text-gray-200">{session.ipAddress || '-'}</div>
                                </div>
                                <button
                                    className="rounded bg-red-600 hover:bg-red-700 text-white px-3 py-2"
                                    onClick={async () => {
                                        try {
                                            await revokeMySession(session.id)
                                            toast.success(isHebrew ? 'הסשן בוטל' : 'Session revoked')
                                            await load()
                                        } catch (error: any) {
                                            toast.error(error?.message || 'Failed')
                                        }
                                    }}
                                >
                                    {isHebrew ? 'נתק' : 'Revoke'}
                                </button>
                            </div>
                        ))}
                        {sessions.length === 0 && <div className="text-gray-600 dark:text-gray-300">{isHebrew ? 'אין סשנים פעילים' : 'No active sessions'}</div>}
                    </div>
                )}
            </div>
        </div>
    )
}
